import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useTheme } from "@/context/ThemeContext";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import { TrendingUp, Image as ImageIcon } from "lucide-react";

const eur = (n) => {
  const num = Number(n || 0);
  const [i, d] = Math.abs(num).toFixed(2).split(".");
  return `${num < 0 ? "-" : ""}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${d} €`;
};
const imgSrc = (im) => (im?.storage_path ? fileUrl(im.storage_path) : im?.url);

export function ResellerSalesSummary() {
  const { t } = useI18n();
  const { theme } = useTheme();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/reports/my-sales-stats").then(({ data }) => setD(data)).catch(() => {});
  }, []);

  if (!d || (d.orders || 0) === 0) return null;
  const trend = (d.trend || []).map((m) => ({ name: m.label, value: m.spend }));

  return (
    <section className="mt-10" data-testid="reseller-sales-summary">
      <h2 className="mb-4 text-lg font-semibold text-foreground">{t("dash.stats_title")}</h2>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-brema-card p-6 lg:col-span-2">
          <div className="mb-1 flex items-center gap-2 text-foreground">
            <TrendingUp className="h-5 w-5 text-brand" />
            <h3 className="font-semibold">{t("dash.stats_trend")}</h3>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">{t("dash.stats_trend_desc")}</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trend}>
              <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => eur(v)}
                contentStyle={{ background: "#1D2433", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {trend.map((_, i) => (<Cell key={i} fill={theme === "dark" ? "#FACC15" : "#B91C1C"} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-border bg-brema-card p-6">
          <h3 className="mb-4 font-semibold text-foreground">{t("dash.stats_top")}</h3>
          <div className="space-y-3" data-testid="reseller-top-products">
            {d.top_products.map((p, i) => (
              <div key={p.product_id || i} className="flex items-center gap-3">
                <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-border bg-brema-bg">
                  {imgSrc(p.image)
                    ? <img src={imgSrc(p.image)} alt={p.name} className="h-full w-full object-cover" />
                    : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-4 w-4" /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.units} pz · {eur(p.spend)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
